/* ==========================================================================
   EPAspeed alternative banner
   --------------------------------------------------------------------------
   Runs on every product page and does three jobs:

   1. A/B selection - picks one of the banners rendered on a competitor page,
      weighted, and keeps that choice sticky per visitor so the experiment is
      not polluted by people seeing a different arm on every visit.

   2. Measurement - appends UTMs to the CTA and publishes view/click events
      through Shopify.analytics.publish. The store's GA4 runs inside a Web
      Pixel sandbox, so a theme-level gtag() call would never reach it; the
      Custom Pixel in pixels/epa-ga4-pixel.js subscribes to these events and
      forwards them.

   3. Order attribution - when someone clicks through, the banner key is
      stored and then written onto the EPAspeed product's cart line as a
      hidden line item property, so the banner that earned a sale is visible
      on the order itself rather than inferred from session attribution.
   ========================================================================== */
(function () {
  'use strict';

  var AB_KEY_PREFIX = 'epa_ab_v1_';
  var ATTR_KEY = 'epa_attr_v1';
  var VIEW_THRESHOLD = 0.5;
  var VIEW_DWELL_MS = 1000;
  var SLOT = 'pdp_alt_banner';

  /* --- storage helpers (private mode / disabled storage safe) ------------ */

  function readStore(key) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* storage unavailable - the banner still renders, we just lose stickiness */
    }
  }

  function currentProductHandle() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  /* --- analytics -------------------------------------------------------- */

  function publish(name, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) {
      /* never let a tracking failure break the page */
    }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, payload));
    } catch (e) {
      /* no-op */
    }
  }

  function payloadFor(el, root) {
    return {
      promotion_id: el.getAttribute('data-epa-variant'),
      promotion_name: el.getAttribute('data-epa-name') || el.getAttribute('data-epa-variant'),
      creative_name: el.getAttribute('data-epa-style'),
      creative_slot: SLOT,
      campaign: el.getAttribute('data-epa-campaign'),
      source_product_handle: root.getAttribute('data-epa-source-handle'),
      source_product_title: root.getAttribute('data-epa-source-title'),
      target_product_handle: el.getAttribute('data-epa-target-handle'),
      target_product_id: el.getAttribute('data-epa-target-id'),
      target_product_title: el.getAttribute('data-epa-target-title'),
      target_product_price: parseFloat(el.getAttribute('data-epa-target-price')) || null
    };
  }

  /* --- A/B selection ---------------------------------------------------- */

  function weightOf(el) {
    var raw = parseInt(el.getAttribute('data-epa-weight'), 10);
    if (isNaN(raw) || raw < 0) return 1;
    return raw;
  }

  function pickWeighted(banners) {
    var total = 0;
    var i;
    for (i = 0; i < banners.length; i++) total += weightOf(banners[i]);

    // Every weight is 0 - treat that as an even split rather than showing nothing.
    if (total <= 0) return banners[Math.floor(Math.random() * banners.length)];

    var roll = Math.random() * total;
    for (i = 0; i < banners.length; i++) {
      roll -= weightOf(banners[i]);
      if (roll < 0) return banners[i];
    }
    return banners[banners.length - 1];
  }

  function findByVariant(banners, key) {
    for (var i = 0; i < banners.length; i++) {
      if (banners[i].getAttribute('data-epa-variant') === key) return banners[i];
    }
    return null;
  }

  function chooseBanner(root, banners) {
    // QA override: ?epa_preview=<variant key> forces one arm without touching storage.
    var forced = new URLSearchParams(window.location.search).get('epa_preview');
    if (forced) {
      var match = findByVariant(banners, forced);
      if (match) return match;
    }

    // The theme editor re-renders constantly; never persist a choice made there.
    if (window.Shopify && window.Shopify.designMode) return banners[0];

    var storeKey = AB_KEY_PREFIX + root.getAttribute('data-epa-product-id');
    var saved = readStore(storeKey);

    if (saved && saved.v) {
      var existing = findByVariant(banners, saved.v);
      if (existing) return existing;
      // The saved arm was removed or renamed - fall through and re-roll.
    }

    var chosen = pickWeighted(banners);
    writeStore(storeKey, { v: chosen.getAttribute('data-epa-variant'), ts: Date.now() });
    return chosen;
  }

  /* --- CTA link tagging ------------------------------------------------- */

  function tagCta(el, root) {
    var cta = el.querySelector('[data-epa-cta]');
    if (!cta) return null;

    var base = cta.getAttribute('data-epa-base-href') || cta.getAttribute('href');
    if (!base) return null;

    var url;
    try {
      url = new URL(base, window.location.origin);
    } catch (e) {
      return cta;
    }

    url.searchParams.set('utm_source', el.getAttribute('data-epa-utm-source') || 'onsite');
    url.searchParams.set('utm_medium', el.getAttribute('data-epa-utm-medium') || 'alt_banner');
    url.searchParams.set('utm_campaign', el.getAttribute('data-epa-campaign') || 'epaspeed_alternative');
    url.searchParams.set('utm_content', el.getAttribute('data-epa-variant') || '');
    url.searchParams.set('utm_term', root.getAttribute('data-epa-source-handle') || '');

    cta.setAttribute('href', url.pathname + url.search);
    return cta;
  }

  /* --- view + click tracking -------------------------------------------- */

  function trackView(el, root) {
    var fired = false;

    function fire() {
      if (fired) return;
      fired = true;
      publish('epa_banner_view', payloadFor(el, root));
    }

    if (!('IntersectionObserver' in window)) {
      fire();
      return;
    }

    var timer = null;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= VIEW_THRESHOLD) {
            if (timer === null) {
              timer = window.setTimeout(function () {
                fire();
                observer.disconnect();
              }, VIEW_DWELL_MS);
            }
          } else if (timer !== null) {
            window.clearTimeout(timer);
            timer = null;
          }
        });
      },
      { threshold: [VIEW_THRESHOLD] }
    );

    observer.observe(el);
  }

  function trackClick(cta, el, root) {
    if (!cta) return;

    cta.addEventListener('click', function () {
      var payload = payloadFor(el, root);
      publish('epa_banner_click', payload);

      if (root.getAttribute('data-epa-order-attribution') === '1') {
        writeStore(ATTR_KEY, {
          v: payload.promotion_id,
          style: payload.creative_name,
          campaign: payload.campaign,
          from: payload.source_product_handle,
          to: payload.target_product_handle,
          ts: Date.now()
        });
      }
    });
  }

  /* --- order attribution ------------------------------------------------ */

  function attributionWindowMs(root) {
    var hours = root ? parseInt(root.getAttribute('data-epa-attribution-hours'), 10) : NaN;
    if (isNaN(hours) || hours <= 0) hours = 24;
    return hours * 60 * 60 * 1000;
  }

  function setHidden(form, name, value) {
    var existing = form.querySelector('input[name="' + name + '"]');
    if (existing) {
      existing.value = value;
      return;
    }
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  // The page carries add-to-cart forms for recommended products as well as for
  // the product itself. Only the product the banner pointed at earned the
  // attribution, so the others must be left alone.
  function mainVariantIds() {
    try {
      var el = document.querySelector('script[id^="ProductJson-"]');
      if (!el) return null;
      var variants = JSON.parse(el.textContent).variants || [];
      var ids = {};
      for (var i = 0; i < variants.length; i++) ids[String(variants[i].id)] = true;
      return ids;
    } catch (e) {
      return null;
    }
  }

  function isMainProductForm(form, ids) {
    if (ids) {
      var input = form.querySelector('[name="id"]');
      if (input && ids[String(input.value)]) return true;
    }
    // Fallback for markup where the variant input is not populated yet.
    return form.hasAttribute('data-product-form');
  }

  function stampForms(attr) {
    var ids = mainVariantIds();
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (!isMainProductForm(forms[i], ids)) continue;
      // Leading underscore keeps these off the cart, checkout and customer
      // emails while still landing on the order in admin and in exports.
      setHidden(forms[i], 'properties[_epa_banner]', attr.v);
      setHidden(forms[i], 'properties[_epa_banner_style]', attr.style || '');
      setHidden(forms[i], 'properties[_epa_banner_from]', attr.from || '');
    }
  }

  function applyOrderAttribution(root) {
    var attr = readStore(ATTR_KEY);
    if (!attr || !attr.v || !attr.to) return;

    if (Date.now() - (attr.ts || 0) > attributionWindowMs(root)) {
      try {
        window.localStorage.removeItem(ATTR_KEY);
      } catch (e) {
        /* no-op */
      }
      return;
    }

    // Only stamp the product the banner actually pointed at.
    if (currentProductHandle() !== attr.to) return;

    stampForms(attr);

    // Quick view and variant swaps can replace the form after load. Coalesce
    // into one pass per frame so a chatty app script cannot turn this into a
    // DOM query storm.
    if ('MutationObserver' in window) {
      var queued = false;
      var observer = new MutationObserver(function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          stampForms(attr);
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  /* --- boot ------------------------------------------------------------- */

  function init() {
    var root = document.querySelector('[data-epa-alt-root]');

    // Runs on the EPAspeed page too, where there is no banner to render.
    applyOrderAttribution(root);

    if (!root) return;

    var banners = Array.prototype.slice.call(root.querySelectorAll('[data-epa-banner]'));
    if (!banners.length) return;

    var chosen = chooseBanner(root, banners);
    if (!chosen) return;

    banners.forEach(function (el) {
      if (el !== chosen) el.parentNode.removeChild(el);
    });

    chosen.removeAttribute('hidden');

    var cta = tagCta(chosen, root);
    trackClick(cta, chosen, root);

    if (root.getAttribute('data-epa-ga4') === '1') {
      trackView(chosen, root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
